import {
  Box,
  ButtonBase,
  CircularProgress,
  Divider,
  Fade,
  TextField,
  Typography,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "react-query";
import { useSnackbar } from "notistack";
import { FeatureGuard, WebBackButton } from "../../components/shared";
import { useTelegram } from "../../hooks";
import { createSbpPayment } from "../../services/payments";
import { decodeQrFromImage } from "../../components/pages/pay-qr/decodeQrFromImage";
import { useTranslation } from "react-i18next";
import { IS_WEB } from "../../utils/runtime";
import jsQR from "jsqr";

function isProbablySbpPayload(v: string): boolean {
  const s = v.trim();
  if (s.length < 8) return false;
  // Основной формат СБП — deeplink https://qr.nspk.ru/…
  // Но принимаем и просто длинные строки — окончательную валидацию сделает бэк.
  return true;
}

function PayQrPageContent() {
  const navigate = useNavigate();
  const { tg } = useTelegram();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation("payqr");

  const [manualValue, setManualValue] = useState("");
  const [decodingImage, setDecodingImage] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const scanRafRef = useRef<number>(0);

  useEffect(() => {
    const handleBack = () => navigate("/");
    tg.BackButton.show();
    tg.onEvent("backButtonClicked", handleBack);
    return () => {
      tg.BackButton.hide();
      tg.offEvent("backButtonClicked", handleBack);
    };
  }, [navigate, tg]);

  const { mutate: submit, isLoading: isSubmitting } = useMutation(createSbpPayment, {
    onSuccess: (payment) => {
      stopCamera();
      navigate(`/pay-qr/${payment.id}`, { replace: true });
    },
    onError: (err: unknown) => {
      const data = (err as { response?: { data?: { message?: string } } })?.response
        ?.data;
      const msg =
        typeof data?.message === "string"
          ? data.message
          : t("errors.create_failed");
      enqueueSnackbar(msg, { variant: "error" });
    },
  });

  const stopCamera = useCallback(() => {
    cancelAnimationFrame(scanRafRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setCameraOpen(false);
  }, []);

  useEffect(() => () => stopCamera(), [stopCamera]);

  const startWebCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment", width: { ideal: 1280 } },
      });
      streamRef.current = stream;
      setCameraOpen(true);

      await new Promise<void>((resolve) => {
        const check = () => {
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
            videoRef.current.play();
            resolve();
          } else {
            requestAnimationFrame(check);
          }
        };
        check();
      });

      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d", { willReadFrequently: true })!;

      const tick = () => {
        const video = videoRef.current;
        if (!video || video.readyState < 2) {
          scanRafRef.current = requestAnimationFrame(tick);
          return;
        }
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        ctx.drawImage(video, 0, 0);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const result = jsQR(imageData.data, canvas.width, canvas.height, {
          inversionAttempts: "attemptBoth",
        });
        if (result?.data) {
          submit({ qrPayload: result.data.trim() });
          return;
        }
        scanRafRef.current = requestAnimationFrame(tick);
      };
      scanRafRef.current = requestAnimationFrame(tick);
    } catch {
      enqueueSnackbar(t("errors.scanner_failed"), { variant: "error" });
    }
  }, [submit, enqueueSnackbar, t]);

  const handleScan = () => {
    if (IS_WEB) {
      if (cameraOpen) {
        stopCamera();
      } else {
        startWebCamera();
      }
      return;
    }
    try {
      const anyTg = tg as unknown as {
        showScanQrPopup?: (
          params: { text?: string },
          cb: (data: string) => boolean | void,
        ) => void;
        closeScanQrPopup?: () => void;
      };
      if (typeof anyTg.showScanQrPopup !== "function") {
        enqueueSnackbar(t("errors.scanner_unsupported"), { variant: "warning" });
        return;
      }
      anyTg.showScanQrPopup({ text: t("scan_hint") }, (data) => {
        if (!data) return true;
        anyTg.closeScanQrPopup?.();
        submit({ qrPayload: data });
        return true;
      });
    } catch {
      enqueueSnackbar(t("errors.scanner_failed"), { variant: "error" });
    }
  };

  const handleContinue = () => {
    const value = manualValue.trim();
    if (!value) return;
    if (!isProbablySbpPayload(value)) {
      enqueueSnackbar(t("errors.short_payload"), { variant: "warning" });
      return;
    }
    submit({ qrPayload: value });
  };

  const handlePickFile = () => fileInputRef.current?.click();

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // сбросить, чтобы повторный пик того же файла срабатывал
    if (!file) return;
    setDecodingImage(true);
    try {
      const payload = await decodeQrFromImage(file);
      submit({ qrPayload: payload });
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : t("errors.read_failed");
      enqueueSnackbar(msg, { variant: "error" });
    } finally {
      setDecodingImage(false);
    }
  };

  return (
    <Fade in>
      <Box
        sx={{
          width: "100%",
          paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 220px)",
        }}
      >
        <WebBackButton to="/" title={t("title")} />
        <Typography
          sx={{
            textAlign: "center",
            fontSize: "13px",
            color: "text.secondary",
            marginBottom: "20px",
            padding: "0 20px",
          }}
        >
          {t("subtitle")}
        </Typography>

        <ScanCard
          onClick={handleScan}
          loading={isSubmitting || decodingImage}
          cameraOpen={cameraOpen}
          videoRef={videoRef}
          onStopCamera={stopCamera}
        />

        <Box sx={{ marginTop: "12px" }}>
          <GalleryButton
            onClick={handlePickFile}
            loading={decodingImage}
            disabled={isSubmitting}
          />
        </Box>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={handleFileSelected}
        />

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            marginBlock: "22px",
            paddingInline: "8px",
          }}
        >
          <Divider sx={{ flex: 1 }} />
          <Typography sx={{ fontSize: "12px", color: "text.secondary" }}>
            {t("manual_hint")}
          </Typography>
          <Divider sx={{ flex: 1 }} />
        </Box>

        <TextField
          fullWidth
          placeholder={t("manual_placeholder")}
          value={manualValue}
          onChange={(e) => setManualValue(e.target.value)}
          disabled={isSubmitting}
          sx={(theme) => ({
            "& .MuiOutlinedInput-root": {
              borderRadius: "16px",
              backgroundColor: theme.palette.secondary.main,
              fontSize: "14px",
              "& fieldset": {
                borderColor:
                  theme.palette.mode === "light"
                    ? "rgba(0, 0, 0, 0.06)"
                    : "transparent",
              },
              "&:hover fieldset": {
                borderColor:
                  theme.palette.mode === "light"
                    ? "rgba(0, 0, 0, 0.12)"
                    : "rgba(255, 255, 255, 0.1)",
              },
              "&.Mui-focused fieldset": {
                borderColor: theme.palette.primary.main,
                borderWidth: "1px",
              },
            },
            "& .MuiOutlinedInput-input": { padding: "16px" },
          })}
        />

        <ButtonBase
          onClick={handleContinue}
          disabled={!manualValue.trim() || isSubmitting}
          sx={(theme) => ({
            width: "100%",
            marginTop: "12px",
            padding: "16px",
            borderRadius: "16px",
            fontSize: "15px",
            fontWeight: 700,
            color: manualValue.trim()
              ? theme.palette.primary.contrastText
              : theme.palette.text.secondary,
            bgcolor: manualValue.trim()
              ? theme.palette.primary.main
              : theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.06)"
                : "rgba(0, 0, 0, 0.05)",
            transition: "opacity 0.15s",
            "&:active": { opacity: manualValue.trim() ? 0.85 : 1 },
            position: "relative",
            zIndex: 100005,
          })}
        >
          {isSubmitting ? (
            <CircularProgress size={20} sx={{ color: "inherit" }} />
          ) : (
            t("continue")
          )}
        </ButtonBase>
      </Box>
    </Fade>
  );
}

function ScanCard({
  onClick,
  loading,
  cameraOpen,
  videoRef,
  onStopCamera,
}: {
  onClick: () => void;
  loading: boolean;
  cameraOpen: boolean;
  videoRef: React.RefObject<HTMLVideoElement>;
  onStopCamera: () => void;
}) {
  const { t } = useTranslation("payqr");

  if (cameraOpen) {
    return (
      <Box
        sx={(theme) => ({
          width: "100%",
          borderRadius: "24px",
          overflow: "hidden",
          backgroundColor: "#000",
          border:
            theme.palette.mode === "light"
              ? "1px solid rgba(0, 0, 0, 0.06)"
              : "1px solid rgba(51, 119, 255, 0.3)",
          boxShadow: "0 10px 40px -20px rgba(51, 119, 255, 0.7)",
          position: "relative",
        })}
      >
        <Box
          component="video"
          ref={videoRef}
          autoPlay
          playsInline
          muted
          sx={{
            width: "100%",
            display: "block",
            maxHeight: 360,
            objectFit: "cover",
          }}
        />
        {/* Рамка прицела */}
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "60%",
            maxWidth: 200,
            aspectRatio: "1",
            border: "2px solid rgba(255,255,255,0.6)",
            borderRadius: "16px",
            pointerEvents: "none",
          }}
        />
        <ButtonBase
          onClick={onStopCamera}
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            width: 36,
            height: 36,
            borderRadius: "50%",
            bgcolor: "rgba(0,0,0,0.5)",
            color: "#fff",
            backdropFilter: "blur(8px)",
          }}
        >
          <Icon icon="solar:close-circle-bold" width={24} height={24} />
        </ButtonBase>
        <Typography
          sx={{
            position: "absolute",
            bottom: 16,
            left: 0,
            right: 0,
            textAlign: "center",
            color: "#fff",
            fontSize: "13px",
            fontWeight: 500,
            textShadow: "0 1px 4px rgba(0,0,0,0.6)",
          }}
        >
          {t("scan_hint")}
        </Typography>
      </Box>
    );
  }

  return (
    <ButtonBase
      onClick={loading ? undefined : onClick}
      disabled={loading}
      sx={(theme) => ({
        width: "100%",
        padding: "24px 16px",
        borderRadius: "24px",
        backgroundColor: theme.palette.secondary.main,
        border:
          theme.palette.mode === "light"
            ? "1px solid rgba(0, 0, 0, 0.06)"
            : "1px solid rgba(255, 255, 255, 0.04)",
        boxShadow:
          theme.palette.mode === "dark"
            ? "0 10px 40px -20px rgba(51, 119, 255, 0.7)"
            : "0 10px 40px -20px rgba(51, 119, 255, 0.35)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "8px",
        transition: "transform 0.15s",
        "&:active": { transform: "scale(0.98)" },
      })}
    >
      <Box
        sx={(theme) => ({
          width: 56,
          height: 56,
          borderRadius: "18px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor:
            theme.palette.mode === "dark"
              ? "rgba(51, 119, 255, 0.16)"
              : "rgba(51, 119, 255, 0.1)",
          color: theme.palette.primary.main,
          marginBottom: "6px",
        })}
      >
        <Icon icon="solar:qr-code-bold-duotone" width={30} height={30} color="currentColor" />
      </Box>
      <Typography sx={{ fontSize: "20px", fontWeight: 700 }}>
        {t("scan")}
      </Typography>
      <Typography sx={{ fontSize: "13px", color: "text.secondary" }}>
        {t("scan_hint")}
      </Typography>
    </ButtonBase>
  );
}

function GalleryButton({
  onClick,
  loading,
  disabled,
}: {
  onClick: () => void;
  loading: boolean;
  disabled: boolean;
}) {
  const { t } = useTranslation("payqr");
  const isDisabled = loading || disabled;
  return (
    <ButtonBase
      onClick={isDisabled ? undefined : onClick}
      disabled={isDisabled}
      sx={(theme) => ({
        width: "100%",
        padding: "14px 16px",
        borderRadius: "20px",
        backgroundColor: theme.palette.secondary.main,
        border:
          theme.palette.mode === "light"
            ? "1px solid rgba(0, 0, 0, 0.06)"
            : "1px solid rgba(255, 255, 255, 0.04)",
        display: "flex",
        alignItems: "center",
        gap: "14px",
        textAlign: "left",
        opacity: isDisabled ? 0.6 : 1,
        transition: "transform 0.15s, opacity 0.15s",
        "&:active": { transform: isDisabled ? "none" : "scale(0.98)" },
      })}
    >
      <Box
        sx={(theme) => ({
          width: 44,
          height: 44,
          borderRadius: "14px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor:
            theme.palette.mode === "dark"
              ? "rgba(51, 119, 255, 0.16)"
              : "rgba(51, 119, 255, 0.1)",
          color: theme.palette.primary.main,
          flexShrink: 0,
        })}
      >
        {loading ? (
          <CircularProgress size={22} sx={{ color: "inherit" }} />
        ) : (
          <Icon
            icon="solar:gallery-bold-duotone"
            width={24}
            height={24}
            color="currentColor"
          />
        )}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: "15px", fontWeight: 700 }}>
          {loading ? t("gallery_loading") : t("gallery_title")}
        </Typography>
        <Typography sx={{ fontSize: "12px", color: "text.secondary" }}>
          {loading ? t("gallery_processing") : t("gallery_hint")}
        </Typography>
      </Box>
    </ButtonBase>
  );
}

export function PayQrPage() {
  return (
    <FeatureGuard feature="sbp_qr">
      <PayQrPageContent />
    </FeatureGuard>
  );
}
